/**
 * 차시 장면 묶음 둘째(LE01~05 · L01 · L02 · L04 · L07~L12 · L14) — 장면마다 따로 둔 상태를 한 덩어리로 돌린다.
 * 화면은 놀이 시계(0.1초)로 `tickLessons`, Bench 소식이 올 때마다 `frameLessons`(흐르는 글자 · 출발 시각 — 0.1초 시계에 맞추면 칸 · 시각을 놓친다)를 부른다.
 * 모든 장면이 같은 보드 소식으로 함께 돈다(놀이를 바꿔도 진행이 남는다 — lib/scenes 첫 묶음과 같다).
 */
import type { MbFrame } from "./live";
import { iconOf } from "./live";
import type { SceneId } from "./scenes";
import { FRIENDS_GOAL, GATE_ASKS, PAGES_GOAL, SHIPS_GOAL, initialGate, initialGreet, initialHarbor, initialStory, stepGate, stepGreet, stepHarbor, stepStory } from "./little";
import type { Gate, Greet, Harbor, Story } from "./little";
import {
  BOARD_LEN, BRICKS_GOAL, REACTION_GOAL, RPS_ROUNDS, allFaces, fastStarts, goalsDone, initialBall, initialDice, initialGoals, initialLevel,
  initialQuiz, initialReaction, initialRps, stepBall, stepDice, stepGoals, stepLevel, stepQuiz, stepReaction, stepRps,
} from "./games";
import type { Ball, Dice, Goals, Level, Quiz, Reaction, Rps } from "./games";
import { MARQUEE_REPEATS, initialGallery, initialScroll, nameOf, repeatsOf, stepGallery, stepScroll } from "./scroll";
import type { Gallery, Scroll } from "./scroll";
import { DOOR_CODE, initialRemotes, stepRemotes, watchDone } from "./remotes";
import type { Remotes } from "./remotes";
import { bandDone, cheerDone, initialStages, showcaseDone, stepStages, TWINKLE } from "./stages";
import type { Stages } from "./stages";

export interface Lessons {
  readonly greet: Greet;
  readonly story: Story;
  readonly harbor: Harbor;
  readonly gate: Gate;
  readonly dice: Dice;
  readonly scroll: Scroll;
  readonly gallery: Gallery;
  readonly goals: Goals;
  readonly rps: Rps;
  readonly reaction: Reaction;
  readonly quiz: Quiz;
  readonly ball: Ball;
  readonly level: Level;
  /** 리모컨 묶음(L33 · L34 · X01 · X02 · X08 — lib/remotes) */
  readonly remotes: Remotes;
  /** 마지막 묶음(LE06 · LE12 · L23 · L26 · X09 — lib/stages) */
  readonly stages: Stages;
  /** 마지막으로 본 아이콘(L01 명찰의 기분) */
  readonly lastIcon: string | null;
}

export const initialLessons = (): Lessons => ({
  greet: initialGreet(), story: initialStory(), harbor: initialHarbor(), gate: initialGate(), dice: initialDice(),
  scroll: initialScroll(), gallery: initialGallery(), goals: initialGoals(), rps: initialRps(), reaction: initialReaction(),
  quiz: initialQuiz(), ball: initialBall(), level: initialLevel(), remotes: initialRemotes(), stages: initialStages(), lastIcon: null,
});

/** 놀이 시계 한 걸음(0.1초) — 소식이 끊겼으면 frame = null */
export function tickLessons(s: Lessons, dtMs: number, frame: MbFrame | null): Lessons {
  return {
    ...s,
    greet: stepGreet(s.greet, dtMs, frame),
    story: stepStory(s.story, dtMs, frame),
    harbor: stepHarbor(s.harbor, dtMs, frame),
    gate: stepGate(s.gate, frame),
    dice: stepDice(s.dice, dtMs, frame),
    gallery: stepGallery(s.gallery, dtMs, frame),
    goals: stepGoals(s.goals, dtMs, frame),
    rps: stepRps(s.rps, dtMs, frame),
    quiz: stepQuiz(s.quiz, frame),
    ball: stepBall(s.ball, dtMs, frame),
    level: stepLevel(s.level, dtMs, frame),
    remotes: stepRemotes(s.remotes, dtMs, frame),
    stages: stepStages(s.stages, dtMs, frame),
    lastIcon: (frame && iconOf(frame.leds)) ?? s.lastIcon,
  };
}

/** Bench 소식 하나(받은 시각 ms) */
export function frameLessons(s: Lessons, nowMs: number, frame: MbFrame): Lessons {
  return { ...s, scroll: stepScroll(s.scroll, frame), reaction: stepReaction(s.reaction, nowMs, frame) };
}

/** 장면 목표를 채웠나(이 묶음의 장면만 — 첫 묶음은 lib/scenes · lib/play) */
export function lessonDone(id: SceneId, s: Lessons): boolean | null {
  switch (id) {
    case "greet": return s.greet.friends >= FRIENDS_GOAL;
    case "story": return s.story.pages.length >= PAGES_GOAL;
    case "harbor": return s.harbor.ships >= SHIPS_GOAL;
    case "gate": return s.gate.i >= GATE_ASKS.length;
    case "maze": return s.remotes.maze.done;
    case "balance": return s.remotes.balance.done;
    case "marathon": return s.remotes.marathon.done;
    case "door": return s.remotes.door.i >= DOOR_CODE.length;
    case "watch": return watchDone(s.remotes.watch);
    case "cheer": return cheerDone(s.stages.cheer);
    case "melody": return s.stages.melody.i >= TWINKLE.length;
    case "band": return bandDone(s.stages.band);
    case "showcase": return showcaseDone(s.stages.showcase);
    case "hotCold": return s.stages.hotCold.found;
    case "boardGame": return s.dice.pos >= BOARD_LEN;
    case "nameTag": return nameOf(s.scroll) !== null && s.lastIcon !== null;
    case "gallery": return s.gallery.pieces.length >= 1;
    case "marquee": return repeatsOf(s.scroll).n >= MARQUEE_REPEATS;
    case "goals": return goalsDone(s.goals);
    case "rps": return s.rps.rounds.length >= RPS_ROUNDS;
    case "reaction": return fastStarts(s.reaction) >= REACTION_GOAL;
    case "quiz": return s.quiz.passed;
    case "dice": return allFaces(s.dice);
    case "ballMaze": return s.ball.done;
    case "level": return s.level.bricks >= BRICKS_GOAL;
    default: return null;
  }
}

